import type { EmojiItem } from '@tiptap/extension-emoji';
import type { Editor } from '@tiptap/react';

import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import { SmilePlusIcon } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { useTiptapEditor } from '@/hooks/tiptap';
import { shouldShow, isNodeInSchema, hasExtension } from '@/lib/tiptap';

import type { EmojiCommandProps } from '.';

export function useEmojiCommand(providedEditor?: Editor | null) {
  const editor: Editor | null = useTiptapEditor(providedEditor);

  const [searchValue, setSearchValue] = useState<string>('');

  const allEmojis: EmojiItem[] = useMemo<EmojiItem[]>(() => {
    if (!editor?.storage.emoji) return [];
    return editor.storage.emoji.emojis.sort((a, b) => a.name.localeCompare(b.name));
  }, [editor?.storage.emoji]);

  const filteredEmojis: EmojiItem[] = useMemo(() => {
    if (!searchValue) return allEmojis;
    return allEmojis.filter(({ shortcodes, tags }) => {
      return shortcodes.find((shortcode) => shortcode.startsWith(searchValue.toLowerCase())) || tags.find((tag) => tag.startsWith(searchValue.toLowerCase()));
    });
  }, [allEmojis, searchValue]);

  const inSchema: boolean = isNodeInSchema('emoji', editor);
  const hasEmojiExtension: boolean = hasExtension(editor, 'emoji');

  const show: boolean = useMemo(() => {
    return shouldShow([!!editor, inSchema, hasEmojiExtension]);
  }, [editor, inSchema]);

  const handleClickItem = (name: string) => {
    editor?.chain().focus().setEmoji(name).run();
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const clearSearch = () => {
    setTimeout(() => {
      setSearchValue('');
    }, 150);
  };

  return {
    editor,
    searchValue,
    filteredEmojis,
    show,
    handleClickItem,
    handleSearchChange,
    clearSearch,
  };
}

export function EmojiCommand(emojiCommandProps: EmojiCommandProps) {
  const { editor: providedEditor, children, ...props } = emojiCommandProps;
  const parentRef = useRef<HTMLDivElement>(null);

  const { editor, searchValue, filteredEmojis, handleClickItem, handleSearchChange, clearSearch } = useEmojiCommand(providedEditor);

  // Initialize virtualizer for emoji list (can not use in hook, compiler will brake virtualization)
  const virtualizer = useVirtualizer({
    count: filteredEmojis.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 15,
  });

  const virtualOptions: VirtualItem[] = virtualizer.getVirtualItems();

  const handlePopoverOpenChange = (open: boolean) => {
    if (open) {
      requestAnimationFrame(() => {
        virtualizer.measure();
      });
    }
  };

  const inSchema: boolean = isNodeInSchema('emoji', editor);
  const show: boolean = useMemo(() => {
    return shouldShow([!!editor, inSchema]);
  }, [editor, inSchema]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <Popover onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger asChild>
        <Button role="button" data-slot="emoji-button" {...props}>
          {children || <SmilePlusIcon />}
        </Button>
      </PopoverTrigger>
      <PopoverContent disablePortal onInteractOutside={clearSearch} className="max-w-58 rounded-md p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Wyszukaj..." value={searchValue} onValueChange={handleSearchChange} />
          <CommandList>
            {filteredEmojis.length === 0 && searchValue && <CommandEmpty>Nie znaleziono.</CommandEmpty>}
            <CommandGroup ref={parentRef} className="h-64 overflow-auto">
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualOptions.map((virtualItem) => {
                  const emoji: EmojiItem | undefined = filteredEmojis[virtualItem.index];

                  if (!emoji) return null;

                  return (
                    <CommandItem
                      key={virtualItem.index}
                      onSelect={() => handleClickItem(emoji.name)}
                      className="absolute left-0 flex w-full cursor-pointer items-center gap-2"
                      style={{
                        width: '100%',
                        height: `${virtualItem.size}px`,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      {emoji.fallbackImage ? <img src={emoji.fallbackImage} alt={emoji.name} className="size-5" /> : <span className="text-lg">{emoji.emoji}</span>}
                      <span className="truncate">{emoji.name}</span>
                    </CommandItem>
                  );
                })}
              </div>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
